import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface RecordDetailHeaderProps {
  title: string;
  recordType?: string;
  backLabel?: string;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  /**
   * Record actions rendered to the right of the previous/next controls.
   * Each action is opt-in: the button only appears when its handler is
   * provided, so callers gate edit/delete on capability or permission by
   * simply omitting the handler.
   */
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  savePending?: boolean;
  onDelete?: () => void;
  deletePending?: boolean;
  editLabel?: string;
  deleteLabel?: string;
  className?: string;
}

export function RecordDetailHeader({
  title,
  recordType,
  backLabel = 'Back to list',
  canGoPrevious = false,
  canGoNext = false,
  onClose,
  onPrevious,
  onNext,
  isEditing = false,
  onEdit,
  onSave,
  savePending = false,
  onDelete,
  deletePending = false,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  className,
}: RecordDetailHeaderProps) {
  const busy = savePending || deletePending;
  const hasActions = Boolean(onEdit || onDelete);

  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b border-border bg-background/80 px-3 py-2',
        className,
      )}
    >
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={onClose}
        aria-label={backLabel}
        title={backLabel}
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] text-muted-foreground">
          {recordType ? `${recordType} > ${title}` : title}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={onPrevious}
          disabled={!canGoPrevious}
          aria-label="Previous record"
          title="Previous record"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={onNext}
          disabled={!canGoNext}
          aria-label="Next record"
          title="Next record"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
        {hasActions && (
          <>
            <span className="mx-1 h-5 w-px bg-border" aria-hidden />
            {onEdit && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8"
                onClick={onEdit}
                disabled={busy}
              >
                {isEditing ? 'Cancel' : editLabel}
              </Button>
            )}
            {isEditing && onSave && (
              <Button
                type="button"
                size="sm"
                className="h-8 gap-1.5"
                onClick={onSave}
                disabled={savePending}
              >
                {savePending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                Save
              </Button>
            )}
            {onDelete && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="h-8 gap-1.5"
                onClick={onDelete}
                disabled={busy}
              >
                {deletePending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                {deleteLabel}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
